import React from "react";
import type { FC } from "react";

import { getSlotChildPositionBySlotType } from "./getSlotChildPositionBySlotType";
import { UniBoxMainSlot } from "../components/Slot/UniBoxMainSlot";

import type { SlotTypes, SlotChildAlignment } from "./types";
import type { UniBoxMainSlotProps } from "../components/Slot/types";

export type SlotChild = {
    alignmentX?: SlotChildAlignment;
    alignmentY?: SlotChildAlignment;
};

export type FactorySlotProps = Pick<
    UniBoxMainSlotProps,
    "children" | "data-tsid" | "customVarsStyles"
> &
    SlotChild;

type SlotFactoryArgs<SlotType extends SlotTypes> = {
    slotType: SlotType;
    displayName: string;
    defaultTsid: string;
};

/**
 * Создает компонент слота на основе переданных параметров.
 * @param slotType - тип слота, определяющий выравнивание children слота.
 * @param displayName - имя компонента.
 * @param defaultTsid - значение по умолчанию для атрибута data-tsid.
 * @returns компонент слота с заданными параметрами.
 */
export const slotFactory = <SlotType extends SlotTypes>({
    slotType,
    displayName,
    defaultTsid,
}: SlotFactoryArgs<SlotType>) => {
    const defaultSlotChildPosition = getSlotChildPositionBySlotType({
        slotType,
    });

    const FactorySlot: FC<FactorySlotProps> = ({
        alignmentX = defaultSlotChildPosition.horizontalAlignment,
        alignmentY = defaultSlotChildPosition.verticalAlignment,
        "data-tsid": tsid = defaultTsid,
        children,
        customVarsStyles,
    }) => {
        return (
            <UniBoxMainSlot
                data-tsid={tsid}
                alignmentY={alignmentY}
                alignmentX={alignmentX}
                customVarsStyles={customVarsStyles}
            >
                {children}
            </UniBoxMainSlot>
        );
    };

    FactorySlot.displayName = displayName;
    return FactorySlot;
};
